// Référentiel d'allergènes partagé par le site ET le serveur (importé des deux côtés) : la correspondance
// entre l'allergie déclarée par un convive et le contenu réel d'un plat doit donner exactement le même
// résultat dans l'alerte convive, dans la fiche technique cuisine et dans la liste de sélection. Deux copies
// finiraient par diverger, et un écart ici veut dire un convive prévenu à l'écran mais absent de la fiche
// que lit la cuisine — ou l'inverse.
//
// Le problème d'origine (signalé par Emeni, 2026-08-23) : la comparaison était une égalité stricte de texte.
// Un convive déclarant "arachide" n'était PAS alerté par un topping saisi "cacahuète" — ni averti, ni bloqué,
// et l'allergie n'apparaissait même pas sur la fiche cuisine. Silence total des deux côtés.
//
// Deux niveaux, volontairement distincts :
//   - une FAMILLE = un des 14 allergènes réglementaires (INCO 1169/2011), avec ses synonymes ;
//   - ses MEMBRES = les ingrédients concrets qui en relèvent (amande, noisette… pour les fruits à coque).
//
// L'arachide et les fruits à coque sont deux familles SÉPARÉES, comme dans le règlement : l'arachide est une
// légumineuse, et beaucoup de gens sont allergiques à l'une sans l'être à l'autre. Les fusionner déclencherait
// une alerte sur chaque plat contenant de l'amande pour tout allergique aux arachides — à force d'alertes
// injustifiées, plus personne ne les lit et la vraie alerte passe inaperçue.

/** Minuscules, sans accents, sans ponctuation, pluriel simple neutralisé. "Cacahuètes" et "cacahuete"
 *  deviennent le même terme, sans quoi la moindre différence de saisie ferait rater une allergie. */
export const normalizeAllergenTerm = (value) => {
    const base = String(value || '')
        .replace(/œ/gi, 'oe').replace(/æ/gi, 'ae')
        .normalize('NFD').replace(/[̀-ͯ]/g, '')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, ' ')
        .trim();
    // Pluriel retiré mot à mot ("fruits a coque" -> "fruit a coque"), en épargnant les mots trop courts et
    // ceux dont le "s" fait partie du mot (ex: "sulfites" garde son sens au singulier "sulfite", c'est voulu).
    return base.split(' ').map((w) => (w.length > 3 && w.endsWith('s') ? w.slice(0, -1) : w)).join(' ');
};

const F = (canonical, synonyms, members) => ({ canonical, synonyms, members });
const M = (name, synonyms = []) => ({ name, synonyms });

/** Les 14 allergènes réglementaires. Les synonymes couvrent ce qu'un établissement écrit réellement dans sa
 *  liste d'ingrédients et ce qu'un convive tape dans sa déclaration — pas une nomenclature exhaustive. */
export const ALLERGEN_FAMILIES = [
    F('Gluten', ['ble', 'froment', 'cereales contenant du gluten', 'sans gluten'], [
        M('Farine', ['farine de ble']), M('Chapelure', ['panure', 'pane']), M('Pain', ['pain de mie', 'croutons', 'bun']),
        M('Pate', ['pate feuilletee', 'pate brisee', 'pate sablee', 'pate a choux', 'pate a frire']),
        M('Semoule'), M('Orge'), M('Seigle'), M('Avoine'), M('Epeautre'), M('Boulgour'), M('Couscous'), M('Nachos'),
        M('Biscuit', ['speculoos', 'sable', 'biscuit cuillere', 'genoise']), M('Brioche'), M('Orzo'),
        M('Pates', ['spaghetti', 'tagliatelle']), M('Crumble'), M('Pudding'),
    ]),
    F('Crustaces', ['crustace'], [
        M('Crevette', ['crevettes roses']), M('Gambas'), M('Crabe'), M('Homard'), M('Langoustine'), M('Ecrevisse'),
    ]),
    F('Oeuf', ['oeufs', 'blanc d oeuf', 'jaune d oeuf'], [
        M('Mayonnaise', ['mayo']), M('Meringue'), M('Aioli'), M('Sauce cesar'), M('Hollandaise'),
        M('Sauce tartare'), M('Pate a choux'), M('Brioche'), M('Creme patissiere'), M('Pain perdu'),
    ]),
    F('Poisson', ['poissons'], [
        M('Saumon'), M('Cabillaud'), M('Lieu noir', ['lieu']), M('Thon'), M('Anchois'), M('Bar'), M('Dorade'),
        M('Daurade'), M('Truite'), M('Sardine'), M('Sauce nuoc mam', ['nuoc mam']), M('Sauce worcestershire'),
        // Les anchois de la sauce César sont l'allergène caché le plus classique : rien dans le nom du plat
        // ne laisse deviner du poisson dans une salade au poulet.
        M('Sauce cesar'),
    ]),
    // Famille séparée des fruits à coque : c'est précisément le cas signalé par Emeni.
    F('Arachides', ['arachide', 'sans arachide'], [
        // "Cacahuète" est un ingrédient de la famille, pas un autre nom pour elle : déclarer "cacahuète"
        // n'écarte que la cacahuète, déclarer "arachides" écarte toute la famille.
        M('Cacahuete', ['cacahouete', 'peanut', 'eclats de cacahuete', 'cacahuetes concassees']),
        M('Beurre de cacahuete', ['beurre d arachide', 'peanut butter']),
        M('Huile d arachide'),
    ]),
    F('Soja', ['soya'], [
        M('Tofu'), M('Sauce soja', ['shoyu', 'tamari']), M('Edamame'), M('Lecithine de soja'), M('Miso'),
    ]),
    // Libellé "Lait / Lactose" : l'allergène réglementaire s'appelle "Lait", mais les convives déclarent
    // presque toujours "lactose" et Emeni cherchait cette entrée sous ce nom. Les deux mènent ici.
    // "Lactose" est le nom de la FAMILLE, "Lait" un de ses ingrédients. La nuance est celle qu'Emeni
    // décrit : déclarer "lactose" écarte tout ce qui en contient, déclarer "lait" n'écarte que le lait —
    // pas le parmesan ni le beurre. Tant que "lait" était un synonyme de la famille, les deux se
    // comportaient de la même façon.
    F('Lactose', ['produits laitiers', 'sans lactose', 'sans lait'], [
        M('Lait', ['lait de vache', 'lait entier', 'lait demi ecreme']),
        M('Beurre', ['beurre doux', 'beurre demi sel', 'beurre blanc', 'beurre sale']),
        M('Creme', ['creme fraiche', 'creme liquide', 'creme patissiere', 'chantilly', 'creme fouettee']),
        M('Fromage'), M('Parmesan'), M('Cheddar'), M('Mozzarella'), M('Stracciatella'), M('Burrata'),
        M('Mascarpone'), M('Ricotta'), M('Yaourt'), M('Chocolat au lait'), M('Glace', ['creme glacee']),
        M('Chevre'), M('Comte'), M('Emmental'), M('Gruyere'),
        M('Pate a choux'), M('Brioche'), M('Creme patissiere'), M('Pain perdu'), M('Pesto'), M('Bechamel'),
    ]),
    F('Fruits a coque', ['fruit a coque', 'noix diverses', 'oleagineux'], [
        M('Amande', ['poudre d amande', 'amande effilee', 'creme d amande']),
        M('Noisette', ['praline', 'pralin']), M('Pesto'),
        M('Noix', ['cerneaux de noix', 'noix de grenoble']), M('Noix de cajou', ['cajou']),
        M('Pistache'), M('Noix de pecan', ['pecan']), M('Noix du bresil'), M('Noix de macadamia', ['macadamia']),
    ]),
    F('Celeri', ['celeri branche', 'celeri rave'], [M('Sel de celeri'), M('Bouillon de legumes')]),
    // Une même préparation est déclarée dans chaque famille dont elle relève : la mayonnaise porte l'œuf et
    // la moutarde, la sauce César y ajoute les anchois. C'est volontairement redondant avec la famille Œuf
    // ci-dessus — l'index accepte plusieurs rattachements pour un même libellé.
    F('Moutarde', ['graines de moutarde'], [
        M('Moutarde jaune'), M('Moutarde de dijon'), M('Vinaigrette'),
        M('Mayonnaise', ['mayo']), M('Sauce cesar'), M('Sauce tartare'), M('Sauce barbecue'), M('Ketchup'),
    ]),
    F('Sesame', ['graines de sesame'], [M('Tahini', ['puree de sesame']), M('Huile de sesame'), M('Houmous')]),
    F('Sulfites', ['anhydride sulfureux', 'sulfite'], [M('Vin'), M('Vinaigre de vin'), M('Fruits secs')]),
    F('Lupin', ['farine de lupin'], []),
    // Groupes d'ingrédients NON réglementaires, ajoutés pour que les restrictions puissent se composer
    // uniquement à partir d'allergènes : "Végétarien" = Viande + Poisson + Crustacés + Mollusques, "Halal" =
    // Porc + Alcool. Sans eux, une restriction devrait encore lister des mots à la main, ce qui reviendrait à
    // saisir deux fois la même information. Idée d'Emeni (2026-08-23).
    F('Viande', ['viandes', 'chair animale'], [
        M('Boeuf', ['steak', 'steak hache', 'entrecote']), M('Veau'), M('Agneau'), M('Porc'), M('Poulet'),
        M('Volaille'), M('Canard'), M('Dinde'), M('Lapin'), M('Saucisse'), M('Jambon'), M('Bacon'),
        M('Lard'), M('Charcuterie'), M('Saucisson'), M('Chorizo'), M('Gelatine'),
    ]),
    F('Porc', ['cochon'], [
        M('Jambon'), M('Lard'), M('Bacon'), M('Saucisson'), M('Chorizo'), M('Pancetta'), M('Charcuterie'), M('Gelatine'),
    ]),
    F('Alcool', ['boisson alcoolisee'], [
        M('Vin', ['vin blanc', 'vin rouge']), M('Biere', ['guinness']), M('Rhum'), M('Vodka'), M('Whisky'),
        M('Porto'), M('Champagne'), M('Cognac'), M('Liqueur'),
    ]),
    F('Mollusques', ['mollusque'], [
        M('Moule'), M('Huitre'), M('Calamar', ['encornet']), M('Poulpe'), M('Seiche'), M('Saint jacques', ['noix de saint jacques']),
    ]),
];

// Index : terme normalisé -> LISTE de { family, member } (member null si le terme désigne la famille
// elle-même). Une liste, et non une entrée unique, parce qu'une même préparation relève souvent de plusieurs
// allergènes : la mayonnaise porte l'œuf ET la moutarde, la sauce César y ajoute le poisson (anchois). Avec
// une seule famille par terme, l'allergique à la moutarde n'était pas alerté sur des œufs mayonnaise.
const TERM_INDEX = new Map();
const addTerm = (rawTerm, family, member) => {
    const key = normalizeAllergenTerm(rawTerm);
    if (!key) return;
    const list = TERM_INDEX.get(key) || [];
    if (list.some((h) => h.family === family && h.member === member)) return;
    list.push({ family, member });
    TERM_INDEX.set(key, list);
};
ALLERGEN_FAMILIES.forEach((family) => {
    [family.canonical, ...family.synonyms].forEach((t) => addTerm(t, family, null));
    family.members.forEach((member) => {
        [member.name, ...member.synonyms].forEach((t) => addTerm(t, family, member));
    });
});

/** Premier rattachement d'un terme (famille seule, membre précis) ou null. Pour un terme qui relève de
 *  plusieurs allergènes, voir resolveAllergensIn qui les renvoie tous. */
export const resolveAllergenTerm = (term) => (TERM_INDEX.get(normalizeAllergenTerm(term)) || [])[0] || null;

// « Faux amis » : expressions qui contiennent le nom d'un allergène sans en relever. Sans elles, la recherche
// par mot ci-dessous alerterait tous les allergiques au lait sur un curry au LAIT DE COCO — une fausse alerte
// sur un plat parfaitement sûr, et le genre de bruit qui finit par faire ignorer les vraies alertes.
// Seule la famille citée est neutralisée : « lait d'amande » ne déclenche pas Lait, mais déclenche bien
// Fruits à coque via le mot « amande ».
const FALSE_FRIENDS = [
    ['lait de coco', 'Lactose'], ['creme de coco', 'Lactose'], ['eau de coco', 'Lactose'],
    ['lait d amande', 'Lactose'], ['lait de soja', 'Lactose'], ['lait d avoine', 'Lactose'],
    ['lait de riz', 'Lactose'], ['lait vegetal', 'Lactose'],
    ['beurre de cacahuete', 'Lactose'], ['beurre d arachide', 'Lactose'], ['beurre de cacao', 'Lactose'],
    ['fromage vegetal', 'Lactose'], ['creme vegetale', 'Lactose'],
    ['sucre glace', 'Lactose'], // « glace » y est un adjectif, pas une crème glacée
// La famille est résolue à l'usage (findFamily) et non comparée par son libellé : renommer une famille
// — "Lait" est devenu "Lait / Lactose" — désactiverait sinon silencieusement tous ses faux amis, et le lait
// de coco redeviendrait du lait.
].map(([expr, fam]) => [normalizeAllergenTerm(expr), fam]);

// Clés du référentiel triées du plus long au plus court : « noix de cajou » doit primer sur « noix ».
const INDEX_KEYS = Array.from(TERM_INDEX.keys()).sort((a, b) => b.length - a.length);
const containsWords = (haystackWords, needle) => {
    const n = needle.split(' ');
    for (let i = 0; i + n.length <= haystackWords.length; i++) {
        if (n.every((w, j) => haystackWords[i + j] === w)) return true;
    }
    return false;
};

// Familles neutralisées par un faux ami présent dans le libellé, résolues via findFamily pour rester justes
// même si une famille est renommée.
const neutralizedFamilies = (words) => new Set(
    FALSE_FRIENDS
        .filter(([expr]) => containsWords(words, expr))
        .map(([, fam]) => { const f = findFamily(fam); return f ? f.canonical.toLowerCase() : null; })
        .filter(Boolean)
);

const DECOMPOSE_CACHE = new Map();
/**
 * Tous les allergènes repérés DANS un libellé composé. C'est ce qui permet à « Saumon d'Écosse » d'être
 * reconnu comme du poisson et à « Gambas panées » comme un crustacé — la comparaison portait jusqu'ici sur le
 * libellé entier, si bien que la moindre précision ("fumée", "d'Écosse", "panées") faisait perdre l'allergène.
 * Constaté sur la carte réelle (2026-08-23) : 35 allergènes présents ne déclenchaient aucune alerte.
 * La recherche se fait sur des MOTS entiers, jamais sur une sous-chaîne : « pomme » ne doit pas activer
 * « pommeau », et « paille » ne doit rien activer du tout.
 */
export const resolveAllergensIn = (text) => {
    const key = normalizeAllergenTerm(text);
    if (!key) return [];
    const cached = DECOMPOSE_CACHE.get(key);
    if (cached) return cached;

    const words = key.split(' ');
    const neutralized = neutralizedFamilies(words);
    const hits = [];
    const seen = new Set();
    INDEX_KEYS.forEach((k) => {
        if (!containsWords(words, k)) return;
        TERM_INDEX.get(k).forEach((hit) => {
            if (neutralized.has(hit.family.canonical.toLowerCase())) return;
            const id = hit.family.canonical + '|' + (hit.member ? hit.member.name : '');
            if (seen.has(id)) return;
            seen.add(id);
            hits.push(hit);
        });
    });
    DECOMPOSE_CACHE.set(key, hits);
    return hits;
};

const FAMILY_BY_CANONICAL = new Map(ALLERGEN_FAMILIES.map((f) => [normalizeAllergenTerm(f.canonical), f]));

/** Retrouve une famille réglementaire à partir de son libellé (ou d'un de ses synonymes). */
export const findRestrictionGroup = (label) => RESTRICTION_BY_NAME.get(normalizeAllergenTerm(label)) || null;

export const findFamily = (label) => {
    const key = normalizeAllergenTerm(label);
    if (FAMILY_BY_CANONICAL.has(key)) return FAMILY_BY_CANONICAL.get(key);
    const hit = (TERM_INDEX.get(key) || [])[0];
    return hit ? hit.family : null;
};

/**
 * Termes ajoutés par l'établissement, passés en paramètre à chaque appel plutôt que fusionnés dans l'index
 * global : côté serveur un même Worker sert plusieurs établissements, et un index muté par le premier client
 * appliquerait ses préparations maison aux suivants. Chaque terme se comporte comme un MEMBRE de sa famille,
 * donc déclarer "Lait" alerte sur "sauce curry", mais déclarer "sauce curry" n'alerte pas sur du beurre.
 * Format attendu : [{ family: 'Lait', term: 'sauce curry' }, ...]
 */
const customHitsIn = (text, customTerms) => {
    if (!customTerms || !customTerms.length) return [];
    const key = normalizeAllergenTerm(text);
    if (!key) return [];
    const words = key.split(' ');
    const neutralized = neutralizedFamilies(words);
    const hits = [];
    const seen = new Set();
    customTerms.forEach((ct) => {
        if (!ct || ct.disabled) return; // une désactivation ne rattache rien, elle retire (voir removeDisabled)
        const term = normalizeAllergenTerm(ct.term);
        if (!term || !containsWords(words, term)) return;
        const family = findFamily(ct.family);
        if (!family) return;
        if (neutralized.has(family.canonical.toLowerCase())) return;
        // Un LABEL est un autre nom donné à la famille entière ("Produits de la mer" pour Poisson) : il se
        // comporte comme la famille (member: null) et couvre donc tous ses membres. Un TERME ordinaire est un
        // ingrédient précis de plus, qui ne couvre que lui-même.
        const asFamily = ct.kind === 'label';
        const id = family.canonical + '|' + (asFamily ? '' : term);
        if (seen.has(id)) return;
        seen.add(id);
        hits.push({ family, member: asFamily ? null : { name: ct.term, synonyms: [] } });
    });
    return hits;
};

/**
 * Retire les rattachements que l'établissement a désactivés — ex. ne plus traiter "Pesto" comme du lait
 * parce que le sien est sans parmesan. La désactivation vise un couple (famille, ingrédient) précis : le
 * même Pesto reste rattaché aux fruits à coque, seul le lien avec le lait saute.
 * Une désactivation portant sur le libellé de la famille elle-même ("Lait") neutralise toute la famille pour
 * ce terme — c'est assumé, c'est ce que veut dire retirer l'entrée.
 */
const removeDisabled = (hits, customTerms) => {
    if (!customTerms || !customTerms.length) return hits;
    const off = customTerms.filter((ct) => ct && ct.disabled);
    if (!off.length) return hits;
    // Un GROUPE entier retiré par l'établissement (kind 'group' + disabled) neutralise tous ses
    // rattachements d'un coup : plus aucun de ses ingrédients ne déclenche d'alerte. C'est une décision
    // lourde, réversible depuis l'écran de configuration.
    const offGroups = new Set(off.filter((ct) => ct.kind === 'group').map((ct) => normalizeAllergenTerm(ct.family)));
    if (offGroups.size) {
        hits = hits.filter((h) => !offGroups.has(normalizeAllergenTerm(h.family.canonical)));
        if (!hits.length) return hits;
    }
    return hits.filter((h) => !off.some((ct) => {
        if (ct.kind === 'group') return false;
        const family = findFamily(ct.family);
        if (!family || family !== h.family) return false;
        const t = normalizeAllergenTerm(ct.term);
        const memberName = h.member ? normalizeAllergenTerm(h.member.name) : null;
        return memberName ? memberName === t : normalizeAllergenTerm(family.canonical) === t;
    }));
};

/**
 * L'allergie déclarée par un convive doit-elle déclencher une alerte sur ce terme du plat (ingrédient ou
 * trace) ? Règle volontairement ASYMÉTRIQUE :
 *
 *   déclare "arachide"      / plat "cacahuète"        -> oui  (synonymes de la même famille)
 *   déclare "fruits à coque"/ plat "amande"           -> oui  (une famille couvre ses membres)
 *   déclare "amande"        / plat "noisette"         -> NON  (deux allergies distinctes ; alerter ici
 *                                                              noierait les vraies alertes sous du bruit)
 *   déclare "amande"        / plat "fruits à coque"   -> oui  (le plat reste vague, on prévient par prudence)
 *
 * Un terme hors référentiel (ingrédient maison, allergie rare tapée en texte libre) retombe sur l'égalité
 * stricte d'avant : jamais de correspondance devinée sur un mot qu'on ne connaît pas.
 */
export const allergyMatchesTerm = (declaredAllergy, dishTerm, customTerms = null) => {
    const a = normalizeAllergenTerm(declaredAllergy);
    const b = normalizeAllergenTerm(dishTerm);
    if (!a || !b) return false;
    if (a === b) return true;

    // Les deux libellés sont décomposés, car la précision peut se trouver de chaque côté : le convive déclare
    // "fruits à coque" quand le plat dit "Poudre d'amande", mais il peut aussi déclarer "allergie au saumon"
    // face à un plat qui dit "Saumon d'Écosse".
    const hitsA = removeDisabled([...(TERM_INDEX.get(a) || resolveAllergensIn(a)), ...customHitsIn(a, customTerms)], customTerms);
    const hitsB = removeDisabled([...(TERM_INDEX.get(b) || resolveAllergensIn(b)), ...customHitsIn(b, customTerms)], customTerms);
    if (!hitsA.length || !hitsB.length) return false;

    // Règle asymétrique inchangée, appliquée à chaque paire : une famille couvre ses membres, un membre ne
    // couvre pas un autre membre.
    // Les membres se comparent par NOM normalisé, pas par référence : un terme personnalisé produit un objet
    // neuf à chaque appel, deux occurrences du même terme ne seraient jamais reconnues comme identiques.
    const memberKey = (h) => (h.member ? normalizeAllergenTerm(h.member.name) : null);
    return hitsA.some((da) => hitsB.some((db) => {
        if (da.family !== db.family) return false;
        const ka = memberKey(da), kb = memberKey(db);
        if (ka && kb && ka !== kb) return false;
        return true;
    }));
};

/** Libellé de référence d'un terme, pour dédupliquer la liste d'allergies proposée au convive — sans quoi
 *  "Arachides" (venu des traces) et "Cacahuète" (venu des ingrédients) y figurent tous les deux comme s'il
 *  s'agissait de deux allergies différentes. Un terme inconnu se représente lui-même. */
export const canonicalAllergenLabel = (term) => {
    const hit = resolveAllergenTerm(term);
    if (!hit) return String(term || '').trim();
    return hit.member ? hit.member.name : hit.family.canonical;
};

/**
 * L'allergie déclarée touche-t-elle cet ingrédient, directement ou par l'un de ses composants ?
 * Renvoie null si non concerné, sinon { ingredient, via } où "via" nomme le composant fautif quand
 * l'allergène ne vient pas du libellé de l'ingrédient lui-même — la cuisine doit lire "sauce au beurre blanc
 * (contient du lait)" et non un simple "lait" qui ne dirait pas quoi retirer de l'assiette.
 */
// Un composant peut être {name, removable} (nouveau modèle, retrait fin possible) ou une simple chaîne
// (ancien format, jamais retirable indépendamment) — accepté partout où un composant est lu.
const componentName = (comp) => (typeof comp === 'string' ? comp : (comp && comp.name) || '');
const componentRemovable = (comp) => (typeof comp === 'string' ? false : !!(comp && comp.removable));

export const allergyHitsIngredient = (declaredAllergy, ingredient, customTerms = null) => {
    if (!ingredient || !ingredient.name) return null;
    if (allergyMatchesTerm(declaredAllergy, ingredient.name, customTerms)) return { ingredient, via: null };
    const components = Array.isArray(ingredient.components) ? ingredient.components : [];
    if (!components.length) return null;

    // Familles dont le NOM de l'ingrédient est déjà un membre précis : "Lieu noir" est un poisson identifié.
    const precise = new Set(resolveAllergensIn(ingredient.name).filter((h) => h.member).map((h) => h.family.canonical));

    const hit = components.find((comp) => {
        const name = componentName(comp);
        if (!name || !allergyMatchesTerm(declaredAllergy, name, customTerms)) return false;
        // Un composant qui ne fait que redire la CATÉGORIE d'un ingrédient déjà précis n'élargit pas la
        // détection : écrire "Poisson" sous "Lieu noir" documente le plat, ça ne veut pas dire qu'il peut
        // contenir n'importe quel poisson. Sans cette réserve, une allergie aux anchois faisait proposer de
        // retirer le lieu noir — deux poissons différents. Signalé par Emeni (2026-08-23).
        // La prudence reste entière quand l'ingrédient est vague ("Poisson" seul, sans espèce nommée) : là,
        // rien ne permet d'exclure l'espèce à laquelle le convive est allergique.
        const compHits = resolveAllergensIn(name);
        const redundantCategory = compHits.length > 0 && compHits.every((h) => !h.member && precise.has(h.family.canonical));
        return !redundantCategory;
    });
    // "via" porte désormais son propre statut de retirabilité — {name, removable} — indépendant de celui de
    // l'ingrédient parent : un composant marqué "possible de retirer" individuellement (ex: "châtaigne" dans
    // "Toppings") permet un retrait plus précis que celui de toute la préparation. Idée d'Emeni (2026-08-24).
    return hit ? { ingredient, via: { name: componentName(hit), removable: componentRemovable(hit) } } : null;
};

// Restrictions courantes ramenées à des familles d'allergènes : le référentiel sait déjà que "Lieu noir" est
// du poisson et que "Beurre" est du lait, il serait absurde de redemander à l'établissement de le ressaisir
// ingrédient par ingrédient. Les restrictions qui ne correspondent à aucune famille (Halal, Végétarien) ne
// sont volontairement pas listées ici : elles restent traitées par mots-clés, une viande n'étant pas un
// allergène réglementaire.
const RESTRICTION_FAMILIES = [
    [['sans lactose', 'sans lait', 'intolerance au lactose', 'lactose'], ['Lactose']],
    [['sans poisson'], ['Poisson']],
    [['sans gluten'], ['Gluten']],
    [['sans fruits a coque', 'sans oleagineux'], ['Fruits a coque']],
    [['sans arachide'], ['Arachides']],
    [['sans oeuf'], ['Oeuf']],
    [['sans crustaces', 'sans fruits de mer'], ['Crustaces', 'Mollusques']],
    [['sans soja'], ['Soja']],
    [['sans sesame'], ['Sesame']],
    [['sans moutarde'], ['Moutarde']],
    [['sans celeri'], ['Celeri']],
    [['sans sulfites'], ['Sulfites']],
].map(([labels, families]) => [labels.map(normalizeAllergenTerm), families]);

// Restrictions qui ne se ramènent à AUCUN allergène réglementaire : une viande n'est pas un allergène, mais
// elle rend un plat non végétarien ; le vin n'est un allergène que par ses sulfites, mais il rend un plat non
// halal. Ces listes vivaient en dur dans la page convive, invisibles depuis l'interface — Emeni ne pouvait ni
// les consulter ni les corriger, et se demandait à juste titre "comment le système sait que le vin blanc
// c'est de l'alcool ?". Elles sont désormais exposées et complétables dans l'écran de configuration.
export const RESTRICTION_GROUPS = [
    { name: 'Halal', terms: ['porc', 'jambon', 'lard', 'bacon', 'saucisson', 'chorizo', 'pancetta', 'charcuterie', 'gelatine', 'alcool', 'vin', 'vin blanc', 'vin rouge', 'biere', 'rhum', 'vodka', 'whisky', 'porto', 'champagne', 'cognac', 'boeuf', 'poulet', 'volaille', 'veau', 'agneau', 'canard', 'viande'] },
    { name: 'Casher', terms: ['porc', 'jambon', 'lard', 'bacon', 'saucisson', 'chorizo', 'charcuterie', 'crevette', 'gambas', 'crabe', 'homard', 'langoustine', 'moule', 'huitre', 'calamar', 'poulpe', 'fruits de mer'] },
    { name: 'Sans porc', terms: ['porc', 'jambon', 'lard', 'bacon', 'saucisson', 'chorizo', 'pancetta', 'charcuterie', 'gelatine'] },
    { name: 'Sans alcool', terms: ['alcool', 'vin', 'vin blanc', 'vin rouge', 'biere', 'rhum', 'vodka', 'whisky', 'porto', 'champagne', 'cognac', 'guinness'] },
    { name: 'Sans viande', terms: ['boeuf', 'porc', 'poulet', 'volaille', 'veau', 'agneau', 'canard', 'jambon', 'lard', 'bacon', 'saucisson', 'chorizo', 'viande', 'saucisse', 'steak'] },
    { name: 'Végétarien', terms: ['boeuf', 'porc', 'poulet', 'volaille', 'veau', 'agneau', 'canard', 'jambon', 'lard', 'bacon', 'saucisson', 'chorizo', 'viande', 'saucisse', 'steak', 'poisson', 'saumon', 'thon', 'cabillaud', 'lieu noir', 'daurade', 'anchois', 'crevette', 'gambas', 'fruits de mer', 'gelatine'] },
    { name: 'Végétalien', terms: ['boeuf', 'porc', 'poulet', 'volaille', 'veau', 'agneau', 'canard', 'jambon', 'lard', 'bacon', 'viande', 'saucisse', 'steak', 'poisson', 'saumon', 'thon', 'cabillaud', 'lieu noir', 'anchois', 'crevette', 'gambas', 'fruits de mer', 'lait', 'creme', 'fromage', 'beurre', 'oeuf', 'mascarpone', 'yaourt', 'miel', 'gelatine'] },
];

const RESTRICTION_BY_NAME = new Map(RESTRICTION_GROUPS.map((g) => [normalizeAllergenTerm(g.name), g]));

/**
 * Les restrictions réellement proposées au convive : les groupes intégrés que l'établissement a gardés,
 * toute famille d'allergène qu'il a rangée côté "restriction", et ses propres groupes — chacun sous le nom
 * qu'il lui a donné. Extrait de la route d'accueil du convive (2026-08-24) pour que l'écran de
 * configuration du menu teste EXACTEMENT les cases que le convive verra, sans recopier la règle.
 */
export const configuredRestrictionsFrom = (customTerms = []) => {
    const terms = Array.isArray(customTerms) ? customTerms : [];
    const labels = new Map(terms.filter((ct) => ct && ct.kind === 'label' && !ct.disabled).map((ct) => [ct.family, ct.term]));
    const movedKinds = new Map(terms.filter((ct) => ct && ct.kind === 'label' && ct.group_kind && !ct.disabled).map((ct) => [ct.family, ct.group_kind]));
    const removedGroups = new Set(terms.filter((ct) => ct && ct.kind === 'group' && ct.disabled).map((ct) => ct.family.toLowerCase()));
    const ownGroups = terms
        .filter((ct) => ct && ct.kind === 'group' && !ct.disabled && ct.group_kind !== 'allergene' && !removedGroups.has(ct.family.toLowerCase()))
        .map((ct) => labels.get(ct.family) || ct.family);
    const configured = [
        ...RESTRICTION_GROUPS.filter((g) => !removedGroups.has(g.name.toLowerCase()) && (movedKinds.get(g.name) || 'restriction') === 'restriction')
            .map((g) => labels.get(g.name) || g.name),
        ...ALLERGEN_FAMILIES.filter((f) => !removedGroups.has(f.canonical.toLowerCase()) && movedKinds.get(f.canonical) === 'restriction')
            .map((f) => labels.get(f.canonical) || f.canonical),
        ...ownGroups,
    ];
    return configured;
};

/**
 * Familles d'allergènes à tester une par une pour vérifier la couverture d'une carte : celles que
 * l'établissement n'a pas retirées et qu'il n'a pas déplacées côté "restriction" (sinon elles seraient
 * comptées deux fois). Renvoyées sous leur nom d'affichage.
 */
export const configuredAllergenFamiliesFrom = (customTerms = []) => {
    const terms = Array.isArray(customTerms) ? customTerms : [];
    const labels = new Map(terms.filter((ct) => ct && ct.kind === 'label' && !ct.disabled).map((ct) => [ct.family, ct.term]));
    const movedKinds = new Map(terms.filter((ct) => ct && ct.kind === 'label' && ct.group_kind && !ct.disabled).map((ct) => [ct.family, ct.group_kind]));
    const removedGroups = new Set(terms.filter((ct) => ct && ct.kind === 'group' && ct.disabled).map((ct) => ct.family.toLowerCase()));
    return ALLERGEN_FAMILIES
        .filter((f) => !removedGroups.has(f.canonical.toLowerCase()) && movedKinds.get(f.canonical) !== 'restriction')
        .map((f) => labels.get(f.canonical) || f.canonical);
};

/** Mots-clés d'une restriction non-allergène (Halal, Végétarien…), enrichis des termes ajoutés par
 *  l'établissement et privés de ceux qu'il a retirés. */
export const keywordsForRestriction = (restriction, customTerms = null) => {
    const key = normalizeAllergenTerm(restriction);
    let group = RESTRICTION_BY_NAME.get(key);
    // Le nom peut être celui que l'établissement a choisi ("Cascher" pour "Casher") : sans cette résolution,
    // renommer une restriction la privait de tous ses mots-clés et elle ne détectait plus rien.
    if (!group && customTerms) {
        const lbl = customTerms.find((ct) => ct && ct.kind === 'label' && normalizeAllergenTerm(ct.term) === key);
        if (lbl) group = RESTRICTION_BY_NAME.get(normalizeAllergenTerm(lbl.family));
    }
    // Une FAMILLE d'allergène déplacée côté restriction (ex. « Lactose » renommé « Sans lactose ») garde
    // ses ingrédients comme mots-clés : sans cela le groupe déplacé arriverait vide et ne détecterait rien.
    let base = group ? group.terms : [];
    if (!group) {
        let famKey = key;
        if (customTerms) {
            const lbl = customTerms.find((ct) => ct && ct.kind === 'label' && normalizeAllergenTerm(ct.term) === key);
            if (lbl) famKey = normalizeAllergenTerm(lbl.family);
        }
        const fam = ALLERGEN_FAMILIES.find((f) => normalizeAllergenTerm(f.canonical) === famKey);
        if (fam) base = [...fam.synonyms, ...fam.members.map((m) => m.name)];
    }
    if (!customTerms || !customTerms.length) return base;
    // Restriction entièrement retirée par l'établissement : plus aucun mot-clé, donc plus aucune détection.
    const groupKey = group ? normalizeAllergenTerm(group.name) : key;
    if (customTerms.some((ct) => ct && ct.kind === 'group' && ct.disabled && normalizeAllergenTerm(ct.family) === groupKey)) return [];
    // Les termes personnalisés sont rattachés au nom D'ORIGINE du groupe, pas au nom affiché.
    const familyKey = group ? normalizeAllergenTerm(group.name) : key;
    const mine = customTerms.filter((ct) => ct && ct.kind !== 'label' && normalizeAllergenTerm(ct.family) === familyKey);
    const off = new Set(mine.filter((ct) => ct.disabled).map((ct) => normalizeAllergenTerm(ct.term)));
    const added = mine.filter((ct) => !ct.disabled).map((ct) => ct.term);
    return [...base.filter((t) => !off.has(normalizeAllergenTerm(t))), ...added];
};

/** Familles d'allergènes visées par une restriction ("Sans lactose" -> ["Lait"]), vide si la restriction ne
 *  se ramène pas à un allergène (Halal, Végétarien : à traiter par mots-clés). */
export const familiesForRestriction = (restriction, customTerms = null) => {
    let key = normalizeAllergenTerm(restriction);
    if (!key) return [];
    // Une restriction se compose désormais d'ALLERGÈNES sélectionnés ("Vegan" = Œuf + Lactose + Viande +
    // Poisson) plutôt que d'une liste de mots recopiés : l'information n'existe qu'à un seul endroit, et
    // enrichir un allergène profite à toutes les restrictions qui le référencent.
    if (customTerms && customTerms.length) {
        const refs = customTerms
            .filter((ct) => ct && ct.kind === 'allergen_ref' && !ct.disabled && normalizeAllergenTerm(ct.family) === key)
            .map((ct) => ct.term);
        if (refs.length) return refs;
    }
    // Idem : "Sans lactose" renommé doit continuer de viser la famille Lait.
    if (customTerms) {
        const lbl = customTerms.find((ct) => ct && ct.kind === 'label' && normalizeAllergenTerm(ct.term) === key);
        if (lbl) key = normalizeAllergenTerm(lbl.family);
    }
    const found = RESTRICTION_FAMILIES.find(([labels]) => labels.includes(key));
    return found ? found[1] : [];
};

/**
 * Quels ingrédients d'un plat posent problème pour une restriction, et peut-on s'en débarrasser en les
 * retirant ? C'est ce qui permet de PROPOSER "sans sauce au beurre blanc" à un convive sans lactose, plutôt
 * que de lui dire seulement que le plat ne lui convient pas. Chaque entrée : { ingredient, via } comme
 * allergyHitsIngredient. Le repli par mots-clés couvre les restrictions sans équivalent allergène
 * (Végétarien, Halal), appliqué ingrédient par ingrédient pour identifier le coupable et non le plat entier.
 */
export const ingredientsBlockingRestriction = (restriction, ingredients, keywords = [], customTerms = null) => {
    const list = Array.isArray(ingredients) ? ingredients : [];
    const families = familiesForRestriction(restriction, customTerms);
    const out = [];
    list.forEach((ing) => {
        if (!ing || !ing.name) return;
        // 1) Déclaration explicite de l'établissement sur cet ingrédient — prime sur toute déduction.
        const declared = Array.isArray(ing.incompatible_tags) ? ing.incompatible_tags : [];
        if (declared.some((t) => normalizeAllergenTerm(t) === normalizeAllergenTerm(restriction))) {
            out.push({ ingredient: ing, via: null });
            return;
        }
        // 2) La restriction se ramène à un allergène : on réutilise le référentiel.
        for (const fam of families) {
            const hit = allergyHitsIngredient(fam, ing, customTerms);
            if (hit) { out.push(hit); return; }
        }
        // 3) Repli par mots-clés, ingrédient et composants compris.
        if (keywords.length) {
            // Comparaison sur des MOTS entiers, jamais sur une sous-chaîne : "vinaigre" contient la suite de
            // lettres "vin", ce qui faisait signaler les œufs mayo comme incompatibles avec « halal » et
            // « sans alcool ». Signalé par Emeni (2026-08-23). "Vinaigre de vin" reste bien détecté, puisque
            // "vin" y est un mot à part entière.
            const words = [ing.name, ...(Array.isArray(ing.components) ? ing.components.map(componentName) : [])]
                .map((s) => normalizeAllergenTerm(s)).join(' ').split(' ').filter(Boolean);
            const kw = keywords.map(normalizeAllergenTerm).find((k) => k && containsWords(words, k));
            if (kw) out.push({ ingredient: ing, via: null });
        }
    });
    return out;
};

/**
 * Tous les allergènes qu'un ingrédient déclenche réellement, composants compris et termes personnalisés
 * inclus. Une liste vide signifie que le système ne reconnaît RIEN dans cet ingrédient : il ne provoquera
 * aucune alerte, quoi qu'il contienne en vrai. C'est le cas d'un "Boudoir" — plein de gluten, mais un mot
 * que le référentiel ignore. Sert à signaler ces trous au moment de la saisie du plat, plutôt que de les
 * découvrir par un convive non prévenu. Demande d'Emeni (2026-08-23).
 */
export const allergensOfIngredient = (ingredient, customTerms = null) => {
    const parts = typeof ingredient === 'string'
        ? [ingredient]
        : [ingredient && ingredient.name, ...(Array.isArray(ingredient && ingredient.components) ? ingredient.components.map(componentName) : [])];
    const found = new Set();
    parts.filter(Boolean).forEach((term) => {
        [...resolveAllergensIn(term), ...customHitsIn(term, customTerms)].forEach((h) => found.add(h.family.canonical));
    });
    return Array.from(found).sort((a, b) => a.localeCompare(b, 'fr'));
};

/** Libellé de la famille réglementaire d'un terme (null si hors référentiel) — utile pour afficher
 *  "Cacahuète (arachides)" et pour que l'import Gemini rattache un ingrédient à son allergène. */
export const allergenFamilyOf = (term) => {
    const hit = resolveAllergenTerm(term);
    return hit ? hit.family.canonical : null;
};
